import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  KeyRound, 
  Settings, 
  XCircle,
  Download,
  ExternalLink,
  Store,
  Shield,
  List,
  PlusCircle,
  MinusCircle,
  Sparkles,
  Users,
  FileText,
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Upload,
  Layout,
  Tag,
  ShoppingBag,
  Layers,
  BookOpen,
  Pause,
  Power
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../store/authStore';
import Logo from '../../Logo';
import NavItem from './NavItem';
import NavSection from './NavSection';
import NavProfile from './NavProfile';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

interface NavigationProps {
  isMobile?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ 
  isMobile = false, 
  isCollapsed = false,
  onToggleCollapse
}) => {
  const location = useLocation();
  const { isAdmin } = useAuthStore();
  const { t } = useTranslation();
  const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1023px)');

  const mainNavItems = [
    { 
      path: '/', 
      icon: LayoutDashboard, 
      label: t('nav.dashboard'),
      gradient: {
        from: 'from-blue-400',
        to: 'to-blue-600',
        darkFrom: 'dark:from-blue-500',
        darkTo: 'dark:to-blue-700'
      }
    },
    { 
      path: '/help', 
      icon: HelpCircle, 
      label: t('nav.firststeps'), 
      id: 'getting-started-section',
      gradient: {
        from: 'from-teal-400',
        to: 'to-teal-600',
        darkFrom: 'dark:from-teal-500',
        darkTo: 'dark:to-teal-700'
      }
    },
    { 
      path: '/services', 
      icon: ExternalLink, 
      label: t('nav.services'), 
      id: 'services-section',
      gradient: {
        from: 'from-purple-400',
        to: 'to-purple-600',
        darkFrom: 'dark:from-purple-500',
        darkTo: 'dark:to-purple-700'
      }
    },
    { 
      path: '/changelog', 
      icon: Sparkles, 
      label: t('nav.changelog'), 
      id: 'news-section',
      gradient: {
        from: 'from-amber-400',
        to: 'to-amber-600',
        darkFrom: 'dark:from-amber-500',
        darkTo: 'dark:to-amber-700'
      }
    },
    { 
      path: '/documentation-dev', 
      icon: FileText, 
      label: 'Documentation', 
      id: 'documentation-section',
      gradient: {
        from: 'from-indigo-400',
        to: 'to-indigo-600',
        darkFrom: 'dark:from-indigo-500',
        darkTo: 'dark:to-indigo-700'
      }
    },
  ];

  const licenseNavItems = [
    { 
      path: '/activate', 
      icon: KeyRound, 
      label: t('nav.activate'),
      id: 'activate-license',
      gradient: {
        from: 'from-green-400',
        to: 'to-green-600',
        darkFrom: 'dark:from-green-500',
        darkTo: 'dark:to-green-700'
      }
    },
    { 
      path: '/theme-installer', 
      icon: Upload, 
      label: t('nav.installTheme'),
      id: 'install-theme',
      gradient: {
        from: 'from-blue-400',
        to: 'to-blue-600',
        darkFrom: 'dark:from-blue-500',
        darkTo: 'dark:to-blue-700'
      }
    },
    { 
      path: '/update', 
      icon: Settings, 
      label: t('nav.update'),
      id: 'update-theme',
      gradient: {
        from: 'from-violet-400',
        to: 'to-violet-600',
        darkFrom: 'dark:from-violet-500',
        darkTo: 'dark:to-violet-700'
      }
    },
    { 
      path: '/download', 
      icon: Download, 
      label: t('nav.download'),
      id: 'download-theme',
      gradient: {
        from: 'from-blue-400',
        to: 'to-blue-600',
        darkFrom: 'dark:from-blue-500',
        darkTo: 'dark:to-blue-700'
      },
    },
    { 
      path: '/deactivate', 
      icon: XCircle, 
      label: t('nav.deactivate'),
      id: 'deactivate-license',
      gradient: {
        from: 'from-red-400',
        to: 'to-red-600',
        darkFrom: 'dark:from-red-500',
        darkTo: 'dark:to-red-700'
      }
    }
  ];

  const templateNavItems = [
    { 
      path: '/templates', 
      icon: Layers, 
      label: 'Templates Hub', 
      gradient: {
          from: 'from-blue-400',
          to: 'to-blue-600',
          darkFrom: 'dark:from-blue-500',
          darkTo: 'dark:to-blue-700'
      }
    },
    { 
      path: '/page-templates', 
      icon: Layout, 
      label: 'Modèles Pages', 
      gradient: {
          from: 'from-blue-400',
          to: 'to-blue-600',
          darkFrom: 'dark:from-blue-500',
          darkTo: 'dark:to-blue-700'
      }
    },
    { 
      path: '/product-templates', 
      icon: ShoppingBag, 
      label: 'Modèles Produits', 
      gradient: {
          from: 'from-purple-400',
          to: 'to-purple-600',
          darkFrom: 'dark:from-purple-500',
          darkTo: 'dark:to-purple-700'
      }
    },
    { 
      path: '/collection-templates', 
      icon: Tag, 
      label: 'Modèles Collections', 
      gradient: {
          from: 'from-teal-400',
          to: 'to-teal-600',
          darkFrom: 'dark:from-teal-500',
          darkTo: 'dark:to-teal-700'
      }
    },
    { 
      path: '/blog-templates', 
      icon: BookOpen, 
      label: 'Modèles Articles', 
      gradient: {
          from: 'from-yellow-400',
          to: 'to-yellow-600',
          darkFrom: 'dark:from-yellow-500',
          darkTo: 'dark:to-yellow-700'
      }
    }
  ];

  const adminNavItems = [
    { 
      path: '/admin', 
      icon: Shield, 
      label: t('nav.admin.notifications'),
      gradient: {
        from: 'from-rose-400',
        to: 'to-rose-600',
        darkFrom: 'dark:from-rose-500',
        darkTo: 'dark:to-rose-700'
      }
    },
    { 
      path: '/admin/licenses', 
      icon: List, 
      label: t('nav.admin.licensesList'),
      gradient: {
        from: 'from-sky-400',
        to: 'to-sky-600',
        darkFrom: 'dark:from-sky-500',
        darkTo: 'dark:to-sky-700'
      }
    },
    { 
      path: '/admin/users', 
      icon: Users, 
      label: t('nav.admin.usersList'),
      gradient: {
        from: 'from-emerald-400',
        to: 'to-emerald-600',
        darkFrom: 'dark:from-emerald-500',
        darkTo: 'dark:to-emerald-700'
      }
    },
    { 
      path: '/admin/create-license', 
      icon: PlusCircle, 
      label: t('nav.admin.createLicense'),
      gradient: {
        from: 'from-lime-400',
        to: 'to-lime-600',
        darkFrom: 'dark:from-lime-500',
        darkTo: 'dark:to-lime-700'
      }
    },
    { 
      path: '/admin/activate-license', 
      icon: KeyRound, 
      label: t('nav.admin.activateLicense'),
      gradient: {
        from: 'from-green-400',
        to: 'to-green-600',
        darkFrom: 'dark:from-green-500',
        darkTo: 'dark:to-green-700'
      }
    },
    { 
      path: '/admin/deactivate-license', 
      icon: XCircle, 
      label: t('nav.admin.deactivateLicense'),
      gradient: {
        from: 'from-red-400',
        to: 'to-red-600',
        darkFrom: 'dark:from-red-500',
        darkTo: 'dark:to-red-700'
      }
    },
    { 
      path: '/admin/pause-license', 
      icon: Pause, 
      label: 'Mettre en pause',
      gradient: {
        from: 'from-amber-400',
        to: 'to-amber-600',
        darkFrom: 'dark:from-amber-500',
        darkTo: 'dark:to-amber-700'
      }
    },
    { 
      path: '/admin/enable-license', 
      icon: Power, 
      label: 'Réactiver Licence',
      gradient: {
        from: 'from-green-400',
        to: 'to-green-600',
        darkFrom: 'dark:from-green-500',
        darkTo: 'dark:to-green-700'
      }
    },
    { 
      path: '/admin/remove-license', 
      icon: MinusCircle, 
      label: t('nav.admin.removeLicense'),
      gradient: {
        from: 'from-orange-400',
        to: 'to-orange-600',
        darkFrom: 'dark:from-orange-500',
        darkTo: 'dark:to-orange-700'
      }
    }
  ];

  const baseClasses = `h-full bg-white dark:bg-zinc-900 border-r border-gray-200 dark:border-gray-800 z-50 flex flex-col transition-all duration-300`;
  const mobileClasses = isMobile ? 'absolute inset-y-0 left-0 w-64' : 'fixed';
  const collapsedClasses = isCollapsed ? 'w-20' : 'w-64';

  // Afficher le bouton de repli uniquement sur les tablettes
  const showCollapseButton = !isMobile && isTablet && onToggleCollapse;

  return (
    <motion.div 
      initial={isMobile ? false : { x: -280 }}
      animate={{ x: 0 }}
      className={`${baseClasses} ${mobileClasses} ${!isMobile ? collapsedClasses : ''}`}
      data-nav
    >
      <div className="flex items-center justify-between px-6 py-3">
        <Logo className="w-10 h-10 text-gray-900 dark:text-white" />
        
        {showCollapseButton && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onToggleCollapse}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            ) : (
              <ChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            )}
          </motion.button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-2 mask-fade-both relative z-0">
        <NavSection title={isCollapsed ? '' : t('nav.sections.main')}>
          {mainNavItems.map((item) => (
            <NavItem
              key={item.path}
              {...item}
              isActive={location.pathname === item.path}
              isCollapsed={isCollapsed}
            />
          ))}
        </NavSection>

        <NavSection title={isCollapsed ? '' : t('nav.sections.licenses')}>
          {licenseNavItems.map((item) => (
            <NavItem
              key={item.path}
              {...item}
              isActive={location.pathname === item.path}
              id={item.id}
              isCollapsed={isCollapsed}
            />
          ))}
        </NavSection>

        {isAdmin && (
          <NavSection title={isCollapsed ? '' : 'Templates'}>
            {templateNavItems.map((item) => (
              <NavItem
                key={item.path}
                {...item}
                isActive={location.pathname === item.path || (item.path === '/templates' && location.pathname.includes('templates'))}
                isCollapsed={isCollapsed}
              />
            ))}
          </NavSection>
        )}

        {isAdmin && (
          <NavSection title={isCollapsed ? '' : t('nav.sections.admin')}>
            {adminNavItems.map((item) => (
              <NavItem
                key={item.path}
                {...item}
                isActive={location.pathname === item.path}
                isCollapsed={isCollapsed}
              />
            ))}
          </NavSection>
        )}
      </div>

      <div className="px-3 py-4 border-t border-gray-100 dark:border-gray-800 mt-auto flex-shrink-0 relative z-10 bg-white dark:bg-zinc-900">
        <NavProfile isCollapsed={isCollapsed} />
      </div>
    </motion.div>
  );
};

export default Navigation;